
import React, { useState } from 'react';
import { MapPin, Clock, ChevronRight, Camera, CheckSquare, Award, ArrowUpRight, AlertCircle, Calendar, MessageSquare } from 'lucide-react';
import { useVendorMetrics } from '../../hooks/queries/useVendorMetrics';
import { useVendors } from '../../hooks/queries/useCore';
import { useNavigate } from '../../routes';
import { useNavigateTo } from '@/utils/typeSafeNavigation';
import { formatDate, formatNumber } from '@repo/shared-utils';
import { useProperties, useUnits } from '../../hooks/queries/useProperties';
import { getPropertyName } from '@/utils/entityNameHelpers';
import { getUnitDisplayString } from '@repo/shared-utils';
import { useCurrentVendorId } from '../../hooks/queries/useEntityId';
import { getAppNow } from '@/utils/demoClock';
import { PORTAL_EMPTY_STATES } from '@/constants/portalEmptyStates';

type FilterType = 'all' | 'urgent' | 'today' | 'upcoming';

// FilterButton component moved outside to avoid creation during render
const FilterButton: React.FC<{ 
  label: string; 
  type: FilterType; 
  activeFilter: FilterType;
  onClick: (type: FilterType) => void;
}> = ({ label, type, activeFilter, onClick }) => (
  <button 
    onClick={() => onClick(type)}
    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
      activeFilter === type 
        ? 'bg-slate-900 dark:bg-brand-600 text-white shadow-md' 
        : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 bg-white dark:bg-slate-800 border border-transparent'
    }`}
  >
    {label}
  </button>
);

export const VendorDashboardView: React.FC = () => {
  // All hooks must be called before early return
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const navigate = useNavigate();
  const navigateTo = useNavigateTo();

  // Get vendor ID from user account (with linking support)
  const { data: currentVendorId } = useCurrentVendorId();
  
  // Use metrics hook instead of scope hook
  const { data: metrics, isLoading: metricsLoading } = useVendorMetrics(currentVendorId || undefined);
  const { data: vendors = [] } = useVendors();
  const { data: properties = [] } = useProperties();
  const { data: units = [] } = useUnits();

  if (metricsLoading || !metrics) return <div>Loading vendor profile for {currentVendorId}...</div>;

  const profile = vendors.find(v => v.id === currentVendorId);
  if (!profile) return <div>Vendor not found (id={currentVendorId ?? '(none)'} vendors.length={vendors.length})</div>;

  const appNow = getAppNow();
  const todayYmd = appNow.toISOString().split('T')[0];

  // Get jobs from metrics tickets
  const jobs = {
    all: metrics.tickets || [],
    active: metrics.tickets.filter(t => t.status !== 'Completed' && t.status !== 'Verified' && t.status !== 'Approved') || [],
    completed: metrics.tickets.filter(t => t.status === 'Completed' || t.status === 'Verified' || t.status === 'Approved') || [],
    today: metrics.tickets.filter(t => t.createdAt === todayYmd) || []
  };
  
  // Filter logic
  const filteredJobs = jobs.active.filter(job => {
    const today = todayYmd;
    if (activeFilter === 'urgent') return job.priority === 'High' || job.priority === 'Critical';
    if (activeFilter === 'today') return job.createdAt === today;
    if (activeFilter === 'upcoming') return job.createdAt > today;
    return true;
  });
  
  // Calculate Stats dynamically
  const jobsThisMonth = jobs.all.filter(j => {
      const jobDate = new Date(j.createdAt);
      const now = appNow;
      return jobDate.getMonth() === now.getMonth() && jobDate.getFullYear() === now.getFullYear();
  }).length;

  const jobsLastMonth = jobs.all.filter(j => {
      const jobDate = new Date(j.createdAt);
      const now = appNow;
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return jobDate.getMonth() === lastMonth.getMonth() && jobDate.getFullYear() === lastMonth.getFullYear();
  }).length;

  const monthOverMonthChange = jobsLastMonth > 0 
    ? ((jobsThisMonth - jobsLastMonth) / jobsLastMonth) * 100 
    : (jobsThisMonth > 0 ? 100 : 0);

  // Calculate XP to next level
  const xpToNextLevel = (Math.ceil(profile.xp / 1000) * 1000) - profile.xp;

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      {profile.onboardingCompleted === false && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-amber-800 dark:text-amber-200 text-sm">
          <strong>Application under review.</strong> You can complete your profile, upload work photos, add certifications, and set your service area while you wait.
        </div>
      )}
      {/* Header with Stats */}
      <div className="flex flex-col md:flex-row gap-6">
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400">You have {jobs.active.length} active jobs assigned.</p>
        </div>
        
        <div className="flex gap-4">
          <button
            onClick={() => navigateTo('/app/vendor/communication')}
            className="px-4 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition flex items-center"
          >
            <MessageSquare size={16} className="mr-2" /> Messages
          </button>
           <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white p-4 rounded-xl shadow-md min-w-[140px] border border-indigo-500/30">
              <div className="flex items-center justify-between mb-2">
                 <Award size={20} className="text-indigo-200" />
                 <span className="text-xs font-bold bg-white/20 px-2 py-0.5 rounded">Lvl {profile.level}</span>
              </div>
              <div className="text-2xl font-bold">{formatNumber(profile.xp)} XP</div>
              <div className="text-xs text-indigo-200">{formatNumber(xpToNextLevel)} XP to next level</div>
           </div>
           
           <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 rounded-xl shadow-sm min-w-[140px] hidden sm:block">
              <div className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase mb-2">This Month</div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{jobsThisMonth}</div>
              <div className={`flex items-center text-xs font-medium ${
                monthOverMonthChange >= 0 
                  ? 'text-emerald-600 dark:text-emerald-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                <ArrowUpRight size={14} className={`mr-1 ${monthOverMonthChange < 0 ? 'rotate-180' : ''}`} /> 
                {monthOverMonthChange >= 0 ? '+' : ''}{monthOverMonthChange.toFixed(0)}% vs last
              </div>
           </div>
        </div>
      </div>

      {/* Filter / Sort Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-slate-200 dark:border-slate-800 pb-4 gap-4">
        <div className="flex space-x-2 overflow-x-auto w-full sm:w-auto p-1 bg-slate-100 dark:bg-slate-800/50 rounded-xl">
          <FilterButton label="All Work Orders" type="all" activeFilter={activeFilter} onClick={setActiveFilter} />
          <FilterButton label="Urgent" type="urgent" activeFilter={activeFilter} onClick={setActiveFilter} />
          <FilterButton label="Today" type="today" activeFilter={activeFilter} onClick={setActiveFilter} />
          <FilterButton label="Upcoming" type="upcoming" activeFilter={activeFilter} onClick={setActiveFilter} />
        </div>
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Showing <span className="font-bold text-slate-900 dark:text-white">{filteredJobs.length}</span> work orders
        </div>
      </div>

      {/* Job Cards */}
      <div>
        {filteredJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
             {filteredJobs.map(job => (
               <div 
                 key={job.id} 
                 onClick={() => navigate(`/app/jobs/${job.id}`)}
                 className={`bg-white dark:bg-slate-900 rounded-xl border shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col h-full overflow-hidden ${
                   job.priority === 'Critical' 
                     ? 'border-red-200 dark:border-red-900/50 ring-1 ring-red-100 dark:ring-red-900/30' 
                     : 'border-slate-200 dark:border-slate-800 hover:border-brand-300 dark:hover:border-brand-700'
                 }`}
               >
                 {/* Cover Image */}
                 <div className="h-48 bg-slate-100 dark:bg-slate-800 relative overflow-hidden">
                   {(job.images?.length ?? 0) > 0 ? (
                     <img src={job.images?.[0]} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" alt="Work order site" />
                   ) : (
                     <div className="w-full h-full flex items-center justify-center text-slate-300 dark:text-slate-600 bg-slate-50 dark:bg-slate-800">
                       <Camera size={48} />
                     </div>
                   )}
                   <div className="absolute top-3 left-3 flex gap-2">
                     <span className={`px-2.5 py-1 rounded-full text-xs font-bold shadow-sm flex items-center ${
                       job.priority === 'Critical' ? 'bg-red-600 text-white animate-pulse' : 
                       job.priority === 'High' ? 'bg-orange-500 text-white' : 'bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300'
                     }`}>
                       {job.priority === 'Critical' && <AlertCircle size={12} className="mr-1" />}
                       {job.priority} Priority
                     </span>
                     {job.createdAt === todayYmd && (
                       <span className="px-2.5 py-1 rounded-full text-xs font-bold shadow-sm bg-blue-600 text-white flex items-center">
                         <Clock size={12} className="mr-1" /> Today
                       </span>
                     )}
                   </div>
                 </div>
  
                 {/* Content */}
                 <div className="p-5 flex-1 flex flex-col">
                   <h4 className="font-bold text-lg text-slate-900 dark:text-white mb-1 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors line-clamp-1">{job.title}</h4>
                   <div className="flex items-center text-slate-500 dark:text-slate-400 text-sm mb-3">
                     <MapPin size={14} className="mr-1 shrink-0" /> <span className="truncate">{(() => {
                       const propertyName = getPropertyName(job.propertyId, properties) || 'Unknown';
                       const unit = units.find(u => u.id === job.unitId);
                       const property = properties.find(p => p.id === job.propertyId);
                       const unitDisplay = unit && property ? getUnitDisplayString(unit.id, property.type) : 'Unknown';
                       return `${propertyName} | Unit ${unitDisplay}`;
                     })()}</span>
                   </div>
                   
                   <div className="flex-1">
                     <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2 leading-relaxed">{job.description}</p>
                   </div>
  
                   <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                      <div className="flex items-center text-xs font-medium text-slate-500 dark:text-slate-400">
                        <Calendar size={14} className="mr-1" /> {formatDate(job.createdAt)}
                      </div>
                      <div className="flex items-center text-brand-600 dark:text-brand-400 text-sm font-bold group-hover:translate-x-1 transition-transform">
                        Details <ChevronRight size={16} />
                      </div>
                   </div>
                 </div>
               </div>
             ))}
          </div>
        ) : (
           <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 rounded-xl flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 p-12 min-h-[300px] bg-slate-50 dark:bg-slate-800/20">
              <CheckSquare size={48} className="mb-4 text-slate-300 dark:text-slate-600" />
              <span className="font-medium text-lg text-slate-600 dark:text-slate-300">
                {PORTAL_EMPTY_STATES.vendor.noJobsDashboardTitle}
              </span>
              <span className="text-sm mt-1">
                {jobs.active.length === 0
                  ? PORTAL_EMPTY_STATES.vendor.noJobs
                  : PORTAL_EMPTY_STATES.vendor.noJobsDashboardFiltered}
              </span>
              {jobs.active.length > 0 && (
                <button 
                  onClick={() => setActiveFilter('all')}
                  className="mt-4 px-4 py-2 text-brand-600 dark:text-brand-400 font-medium hover:underline"
                >
                  Clear Filters
                </button>
              )}
           </div>
        )}
      </div>
    </div>
  );
};

